<template>
    <div class="calendar-container">
        <div class="calendar-header">
            <toggle :active-tab="mode"
                    class="toggle"
                    @changed="changeMode">
                <tab title="Month">
                    <portal to="content">
                        <month-mode :calendar-date="calendarDate"
                                    :activity-type="activityType"
                                    :get-activities-by-date="getActivitiesByDate"
                                    @select-day="selectDay">
                        </month-mode>
                    </portal>
                </tab>
                <tab title="Week">
                    <portal to="content">
                        <week-mode :calendar-date="calendarDate"
                                   :activity-type="activityType"
                                   :get-activities-by-date-and-time="getActivitiesByDateAndTime"
                                   @select-day="selectDay">
                        </week-mode>
                    </portal>
                </tab>
            </toggle>
            <div class="month-control">
                <img src="../../../assets/img/left-arrow.svg"
                     @click="previous">
                <div class="month-caption">{{ displayDate }}</div>
                <img src="../../../assets/img/right-arrow.svg"
                     @click="next">
            </div>
        </div>
        <portal-target name="content">
        </portal-target>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import * as moment from 'moment/moment';
    import ClickOutside from 'vue-click-outside';
    import { getStartWeekDate, getEndWeekDate, getStartMonthDate } from '@/common/utils/dates';

    export default {
        name: 'ActivitiesCalendar',
        components: {
            Tab:          () => import('@/components/controls/Tab'),
            Toggle:       () => import('@/components/controls/Toggle'),
            WeekMode:     () => import(/* webpackChunkName: "calendar" */ './week/WeekMode'),
            MonthMode:    () => import(/* webpackChunkName: "calendar" */ './month/MonthMode'),
            SwitchButton: () => import(/* webpackChunkName: "calendar" */ '@/components/controls/SwitchButton')
        },
        directives: { ClickOutside },
        props: {
            getActivitiesByDate: {
                type: Function,
                required: true
            },
            getActivitiesByDateAndTime: {
                type: Function,
                required: true
            },
            calendarDate: {
                type: Date,
                required: true
            },
            mode: {
                type: String,
                required: true
            },
            activityType: {
                type: String,
                required: true
            },
            dateFormat: {
                type: String,
                default: 'mm/dd/yyyy'
            }
        },
        computed: {
            currentMonth() {
                return moment(this.calendarDate).format('MMMM YYYY');
            },
            currentWeek() {
                const weekDateFormat = this.dateFormat
                    .split('/')
                    .splice(0, 2)
                    .join('.')
                    .toUpperCase();
                const startWeekDate = getStartWeekDate(this.calendarDate);
                const endWeekDate = getEndWeekDate(this.calendarDate);

                return `${moment(startWeekDate).format(weekDateFormat)} - ${moment(endWeekDate).format(weekDateFormat)}`;
            },
            displayDate() {
                return this.mode === 'Month' ? this.currentMonth : this.currentWeek;
            }
        },
        methods: {
            ...mapActions(['resetActivitiesCalendar']),
            next() {
                const date = this.mode === 'Month'
                    ? moment(this.calendarDate).add(1, 'month').toDate()
                    : moment(this.calendarDate).add(7, 'days').toDate();
                this.$emit('change-date', date);
                this.fetchActivities();
            },
            previous() {
                const date = this.mode === 'Month'
                    ? moment(this.calendarDate).subtract(1, 'month').toDate()
                    : moment(this.calendarDate).subtract(7, 'days').toDate();
                this.$emit('change-date', date);
                this.fetchActivities();
            },
            changeMode(mode) {
                if (mode) {
                    this.$emit('change-mode', mode);
                    this.fetchActivities();
                }
            },
            fetchActivities() {
                this.$emit('fetch-activities');
            },
            selectDay(day) {
                this.$emit('change-mode', 'Week');
                this.$emit('change-date', day);
            }
        },
        beforeDestroy() {
            this.$emit('change-mode', 'Month');
            this.$emit('change-date', new Date());
            this.resetActivitiesCalendar();
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../assets/colors';

    .calendar-container {
        margin-top: 30px;
        align-items: center;
        justify-content: center;

        .calendar-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 26px;

            @media screen and (max-width: 520px ) {
                justify-content: center;
                flex-direction: column;
            }

            .toggle {
                @media screen and (max-width: 520px ) {
                    width: 100%;
                }
            }

            .month-control {
                display: flex;
                align-items: center;
                margin-right: 16px;
                font-size: 16px;
                font-weight: 500;
                letter-spacing: -0.1px;
                color: $Dark;

                img {
                    cursor: pointer;
                    &:first-child {
                        margin-right: 18px;
                    }

                    &:last-child {
                        margin-left: 18px;
                    }
                }

                .month-caption {
                    width: 120px;
                    text-align: center;
                    white-space: nowrap;
                }

                @media screen and (max-width: 520px ) {
                    margin-top: 22px;
                    margin-right: 0;
                    justify-content: center;
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/calendar/ActivitiesCalendar.vue